/**
 * ARIA Sidebar Navigation
 * Version: 1.0.0
 *
 * ARIA Feature implementations:
 * - ARIA-8: Remove Pending Approval from navigation
 * - ARIA-15: Move Technician Profile under Settings menu
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Calendar,
  Users,
  Search,
  Settings,
  FileText,
  Package,
  ClipboardCheck,
  ChevronDown,
  LogOut,
  KeyRound,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useState } from 'react';
import { useAppDispatch, useAppSelector, logout } from '../../store';
import { clearAuthToken } from '../../config/apollo';
import { ChangePasswordModal } from '../auth/ChangePasswordModal';
import { isWarrantyOnly } from '../../config/features';

// Main navigation - ARIA-8: Removed Pending Approval
const navigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Schedule', href: '/booking', icon: Calendar },
  { name: 'Service Orders', href: '/jobs', icon: FileText },
  { name: 'Scheduled', href: '/jobs/scheduled', icon: Calendar },
  { name: 'Pending Parts', href: '/pending-parts', icon: Package },
  { name: 'Pending Debrief', href: '/pending-debrief', icon: ClipboardCheck },
  { name: 'Warranty Lookup', href: '/warranty', icon: Search },
  { name: 'Technicians', href: '/technicians', icon: Users },
];

const settingsNavigation = [
  { name: 'Admin Settings', href: '/admin', icon: Settings },
];

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  // Warranty-Lookup-only deployments (VITE_WARRANTY_ONLY) show just that item; Settings below is unchanged.
  const visibleNavigation = isWarrantyOnly() ? navigation.filter((item) => item.href === '/warranty') : navigation;

  const handleSignOut = () => {
    clearAuthToken();
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-gray-200">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">A</span>
          </div>
          <span className="font-semibold text-gray-900">ARIA</span>
        </Link>
      </div>

      {/* Main navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visibleNavigation.map((item) => {
          const isActive =
            item.href === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.href);

          return (
            <Link
              key={item.name}
              to={item.href}
              className={clsx('sidebar-item', isActive && 'sidebar-item-active')}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.name}</span>
            </Link>
          );
        })}
      </nav>

      {/* Settings section */}
      <div className="px-3 py-4 border-t border-gray-200">
        <button
          onClick={() => setSettingsOpen(!settingsOpen)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600"
        >
          <span>Settings</span>
          <ChevronDown
            className={clsx('w-4 h-4 transition-transform', settingsOpen && 'rotate-180')}
          />
        </button>

        {settingsOpen && (
          <div className="mt-1 space-y-1">
            {settingsNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx('sidebar-item', isActive && 'sidebar-item-active')}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* User info */}
      <div className="px-3 py-4 border-t border-gray-200 space-y-2">
        <div className="flex items-center gap-3 px-3">
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-sm font-medium text-gray-600">
              {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'Unknown user'}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleSignOut}
            title="Sign out"
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={() => setShowChangePassword(true)}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <KeyRound className="w-3.5 h-3.5" />
          Change Password
        </button>
      </div>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </aside>
  );
}
